<script setup lang="ts">
  import { ref, onMounted, onUnmounted, watch, computed, type Ref } from 'vue'
  import _fetch from '@/utils/fetch'
  import MessageServiceCn from '@/components/AppMessageCn/messageCn'
  import { lockScroll, unlockScroll } from '@/utils/scrollLock'
  const emit = defineEmits(['close', 'ok'])
  const $message = MessageServiceCn
  let signing = ref(false)
  const phoneNumber = ref('')
  const phoneCode = ref('')
  // #region 验证码相关
  // 手机验证码状态
  const phoneCodeStatus = ref(false)
  const phoneCodeTime = ref(60)
  let phoneCodeTimer: ReturnType<typeof setInterval> | null = null

  const phoneReg = /^1[3456789]\d{9}$/ // 手机号正则
  const phoneCodeReg = /^\d{6}$/ // 手机验证码正则

  const props = defineProps({
    onSuccess: {
      type: Function,
      default: () => {}
    },
    validateAuthToken: {
      type: String,
      default: ''
    }
  })

  onMounted(() => {
    lockScroll()
  })
  onUnmounted(() => {
    unlockScroll()
  })

  const sendCode = async () => {
    if (phoneSendBtnDisabled.value) return
    // 发送中或倒计时中禁止重复发送
    if (phoneCodeStatus.value) return
    // 根据类型验证格式
    const isValid = phoneReg.test(phoneNumber.value)
    if (!isValid) {
      $message &&
        $message({
          message: '手机号格式错误，请重新输入',
          type: 'error'
        })
      return
    }
    try {
      phoneCodeStatus.value = true
      // 调用验证码接口
      const response = await _fetch('/aigc/api/auth/binding/mobile/code', {
        method: 'POST',
        body: JSON.stringify({
          mobile: phoneNumber.value,
          validateAuthToken: props.validateAuthToken
        })
      })
      const res = await response.json()
      if (res.code !== 200) {
        phoneCodeStatus.value = false
        $message &&
          $message({
            message: res.msg,
            type: 'error'
          })
        return
      }
      // 开始倒计时
      startCountDown(phoneCodeStatus, phoneCodeTime, phoneCodeTimer)
    } catch (error) {
      phoneCodeStatus.value = false
      $message &&
        $message({
          message: '发送失败，请重试',
          type: 'error'
        })
    }
  }

  const startCountDown = (
    codeStatus: Ref<boolean>,
    codeTime: Ref<number>,
    codeTimer: ReturnType<typeof setInterval> | null
  ) => {
    if (codeTimer) {
      clearInterval(codeTimer)
    }

    codeTimer = setInterval(() => {
      if (codeTime.value > 0) {
        codeTime.value--
      } else {
        // 倒计时结束
        codeStatus.value = false
        codeTime.value = 60
        clearInterval(codeTimer!)
        codeTimer = null
      }
    }, 1000)
  }

  const handleConfirm = async () => {
    if (signing.value || !isAllInputsFilled.value) return
    if (phoneNumber.value === '' || phoneCode.value === '') {
      $message({
        message: '请填写手机号和验证码',
        type: 'error'
      })
      return
    }
    signing.value = true
    const response = await _fetch('/aigc/api/auth/binding/mobile/submit', {
      method: 'POST',
      body: JSON.stringify({
        mobile: phoneNumber.value,
        validateAuthToken: props.validateAuthToken,
        code: phoneCode.value
      })
    })
    signing.value = false
    const res = await response.json()
    if (res.code === 200) {
      emit('ok', res.data)
    } else {
      $message({
        message: res.msg,
        type: 'error'
      })
    }
  }

  const phoneSendBtnDisabled = computed(() => {
    return !phoneNumber.value?.trim() || phoneCodeStatus.value
  })

  const isAllInputsFilled = computed(() => {
    return phoneCodeReg.test(phoneCode.value) && phoneReg.test(phoneNumber.value)
  })

  onUnmounted(() => {
    if (phoneCodeTimer) {
      clearInterval(phoneCodeTimer)
      phoneCodeTimer = null
    }
  })
</script>
<template>
  <main class="bindPhone-mask-container" id="bindPhone-mask-container">
    <div class="bindPhone-container" @click.stop="() => {}">
      <a class="detail-close" @click.stop="$emit('close')"><span class="hitems-close"></span></a>
      <div class="bindPhone-header">
        <img class="bindPhone-logo" src="@/assets/images/logo-zhw.webp" height="32" alt="logo" />
      </div>
      <p class="bindPhone-sub-title">绑定手机，保护账号安全</p>
      <div class="bindPhone-content">
        <div class="bindPhone-form">
          <div class="bindPhone-btn">
            <input
              v-model="phoneNumber"
              class="bindPhone-input"
              type="text"
              placeholder="输入手机号"
            />
          </div>
          <div class="bindPhone-btn">
            <input v-model="phoneCode" class="bindPhone-input" placeholder="输入验证码" />
            <span :class="['send-code', { disabled: phoneSendBtnDisabled }]" @click="sendCode()">
              {{ phoneCodeStatus ? `重新发送(${phoneCodeTime}s)` : '发送验证码' }}
            </span>
          </div>
          <div
            @click="handleConfirm"
            class="bindPhone-btn"
            :class="{ 'btn-active': isAllInputsFilled, 'btn-disabled': !isAllInputsFilled }"
          >
            提交
          </div>
        </div>
      </div>
      <p class="bind-phone-text">应监管要求，请您验证并绑定手机号码</p>
    </div>
  </main>
</template>
<style lang="stylus" scoped>
  .bindPhone-mask-container
      display flex
      align-items center
      justify-content center
      width 100%
      height 100vh
      background-color rgba(0, 0, 0, .8)
      position fixed
      top 0
      left 0
      bottom 0
      z-index 999
      &::placeholder
          color #C7C7C7
          font-size 14px
          font-weight: normal
      .bindPhone-container
          font-weight normal !important
          display flex
          flex-direction column
          box-sizing: border-box
          width 420px
          height 596px
          border-radius 12px
          background-color #fff
          box-shadow 0px 4px 10px 0px rgba(0, 0, 0, 0.3)
          padding 8.1rem 5rem 0 5rem
          position relative
          .bindPhone-header
                  text-align center
                  .bindPhone-logo
                      height 3.2rem
          .bindPhone-sub-title
              text-align center
              margin-top 44px
              font-size 16px
              font-weight 600
              color: #101010
          .detail-close
              display: flex
              align-items: center;
              justify-content: center;
              position: absolute;
              width: 4rem;
              height: 4rem;
              top: 0;
              right: -4.6rem;
              border-radius: 50%;
              font-size: 2rem;
              font-weight: 700;
              background-color: #fff;
              transition: all .2s ease-in-out;
              &:hover
                  background-color: #fffc
          .bindPhone-title
              text-align center
              font-size 24px
              font-weight 600
              height 3.6rem
              line-height 3.6rem
              color #101010
          .bindPhone-content
              margin-top 80px
              width 100%
              .bindPhone-form
                  display flex
                  width 100%
                  flex-direction column
                  gap 2rem
              .bindPhone-btn
                  display flex
                  align-items center
                  justify-content center
                  width 100%
                  height 40px
                  border-radius 24px
                  background-color #F6F6FF
                  font-size 14px
                  font-weight 500
                  color #101010
                  position relative
                  &.btn-active
                      background-color var(--primary-color)
                      color #fff
                      cursor pointer
                  &.btn-disabled
                      background-color #C5BEFF
                      color #fff
                      cursor not-allowed
                  .password-icon
                      display inline-block
                      width 20px
                      height 16px
                      background url('@/assets/images/password-close.webp') no-repeat center center
                      background-size 18px
                      margin-right 10px
                      &.close
                          background url('@/assets/images/password.webp') no-repeat center center
                          background-size 18px
                  .bindPhone-input
                      flex 1
                      background-color transparent
                      box-shadow none
                      border none
                      font-size 14px
                      font-weight 500
                      padding 0 16px
                      color #101010
                      &::placeholder
                          color #C7C7C7
                          font-size 14px
                      // 兼容不同浏览器
                      &::-webkit-input-placeholder
                          color #C7C7C7
                          font-size 14px

                      &::-moz-placeholder
                          color #C7C7C7
                          font-size 14px

                      &:-ms-input-placeholder
                          color #C7C7C7
                          font-size 14px
                  .send-code
                      width 90px
                      height 100%
                      border-bottom-right-radius 24px
                      border-top-right-radius 24px
                      line-height 100%
                      background-color var(--primary-color)
                      color #fff
                      display flex
                      justify-content center
                      align-items center
                      font-size 12px
                      cursor pointer
                      &.disabled
                          background-color #c4befa
                          cursor not-allowed
                  input::placeholder
                      font-weight normal !important
                  input::-webkit-input-placeholder
                      font-weight normal !important
                  input::-moz-placeholder
                      font-weight normal !important
                  input:-ms-input-placeholder
                      font-weight normal !important
                  input:-moz-placeholder
          .bind-phone-text
              position absolute
              bottom 2.4rem
              width calc(100% - 10rem)
              font-size:12px
              font-weight 500
              text-align center
              color: #6C6C6C;
  @media screen and (max-width: 500px)
      .bindPhone-mask-container
          height calc(var(--vh, 1vh) * 100)
          align-items end
          overflow-y auto
          .bindPhone-container
              width 100%
              height auto
              min-height calc(var(--vh, 1vh) * 74.73)
              border-radius 12px 12px 0px 0px
              padding: 77px 35px 40px 35px
              padding-bottom calc(env(safe-area-inset-bottom) + 40px)
  @media screen and (min-width: 501px) and (max-width: 1024px)
      .bindPhone-mask-container
          //asas
</style>
